import { registerToken } from '@rsos/maps/providers/esri/identityManager/registerToken';
import { isAuthoritative } from '../authoritative';
import {
  GISServiceTypes,
  type GISService,
  type GISStateLayer,
} from '../gisServicesTypes';
import { LayerLoader } from './LayerLoader';
import { basemapLayerLoader } from './basemapLayerLoader';
import { listSublayers } from './buildLayerMap';
import { defaultLayerLoader } from './defaultLayerLoader';
import { geocodeLoader } from './geocodeLoader';
import { northAmericanTrafficLayerLoader } from './northAmericanTrafficLayerLoader';
import { tileLayerLoader } from './tileLayerLoader';
import { vermontResponderBoundariesLayerLoader } from './vermontResponderBoundariesLayerLoader';
import { wfsLayerLoader } from './wfsLayerLoader';
import { wmsLayerLoader } from './wmsLayerLoader';
import { wmtsLayerLoader } from './wmtsLayerLoader';

export interface LayerResult<T> {
  layer: T;
  service: GISService;
}

export type LayerPromise<T> = Promise<LayerResult<T> | null>;

/**
 * An array of LayerLoaders in order from most specific to most generic. The
 * first loader with a passing test will be applied! If you add a hyper-specific
 * loader to the end of this list, it will not get used.
 */
const layerLoaders: LayerLoader[] = [
  geocodeLoader,
  vermontResponderBoundariesLayerLoader,
  northAmericanTrafficLayerLoader,
  wmsLayerLoader,
  wmtsLayerLoader,
  wfsLayerLoader,
  // Dedicated tiled/vector tiled loader for service_type=5.
  tileLayerLoader,
  basemapLayerLoader,
  defaultLayerLoader,
];

/**
 * Given a GISLayer specification, use the first applicable loader among the
 * layerLoaders
 */
export async function loadLayer(service: GISService): Promise<GISStateLayer[]> {
  const loader = layerLoaders.find(loader => loader.shouldUseLoader(service));
  if (!loader) {
    // eslint-disable-next-line no-console
    console.error('No layer loader available for service:', service);
    return [];
  }
  try {
    return (await loader.load(service)).map(layer => {
      return listSublayers(layer, service);
    });
  } catch (e) {
    // eslint-disable-next-line no-console
    console.error('Failed to load layer:', service);
    // eslint-disable-next-line no-console
    console.error(e);
    return [];
  }
}

/**
 * Extracts the ArcGIS server base URL from a full service URL.
 * e.g., "https://nenaeprc.geocomm.cloud/arcgis/rest/services/..." -> "https://nenaeprc.geocomm.cloud/arcgis"
 */
function extractArcGISServerUrl(serviceUrl: string): string | null {
  const arcgisIndex = serviceUrl.indexOf('/arcgis');
  if (arcgisIndex === -1) return null;
  return serviceUrl.substring(0, arcgisIndex + '/arcgis'.length);
}

/**
 * If there's no basemap service but there are authoritative services with tokens,
 * register those tokens with ESRI's IdentityManager so layer authentication works.
 * This is needed because registerToken is normally only called for basemap services.
 */
function registerGeocommTokensIfNeeded(services: GISService[]): void {
  const hasBasemap = services.some(
    service => service.service_type === GISServiceTypes.basemap,
  );

  // If there's a basemap, the token was already registered in gisServicesAPI.ts
  if (hasBasemap) return;

  // Find authoritative services that have tokens and register them
  const registeredServers = new Set<string>();

  services.forEach(service => {
    if (service.token && isAuthoritative(service)) {
      const serverUrl = extractArcGISServerUrl(service.gis_service_url);
      if (serverUrl && !registeredServers.has(serverUrl)) {
        registerToken(serverUrl, service.token);
        registeredServers.add(serverUrl);
      }
    }
  });
}

/**
 * Load layers in batches to prevent memory spikes on low-memory systems (8GB RAM).
 * This is more memory-friendly than loading all layers in parallel.
 */
async function loadLayersBatched(
  services: GISService[],
  batchSize = 3,
): Promise<GISStateLayer[]> {
  const results: GISStateLayer[] = [];

  for (let i = 0; i < services.length; i += batchSize) {
    const batch = services.slice(i, i + batchSize);
    const batchResults = await Promise.all(batch.map(loadLayer));
    results.push(...batchResults.flat());
  }

  return results;
}

export async function loadLayers(
  services: GISService[],
): Promise<GISStateLayer[]> {
  // Register geocomm tokens if there's no basemap (since registerToken is only
  // called for basemap services in gisServicesAPI.ts)
  registerGeocommTokensIfNeeded(services);

  // Use batched loading to prevent memory spikes on low-memory systems
  return loadLayersBatched(services);
}
